import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../App";
import ClientLayout from "../../components/portal/ClientLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Avatar, AvatarImage, AvatarFallback } from "../../components/ui/avatar";
import OnboardingChecklist from "../../components/portal/OnboardingChecklist";
import BecomePartnerBanner from "../../components/BecomePartnerBanner";
import {
  FileText,
  Clock,
  MessageSquare,
  User,
  DollarSign,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Mail,
  Phone,
  Linkedin,
  ExternalLink,
  ChevronRight,
  Calendar,
  Briefcase,
} from "lucide-react";

const ClientDashboard = () => {
  const { user, token, API } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${API}/client/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setDashboardData(data);
      setShowOnboarding(!data.onboarding_dismissed);
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismissOnboarding = async () => {
    try {
      await fetch(`${API}/client/onboarding/dismiss`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowOnboarding(false);
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <ClientLayout activeNav="dashboard">
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-rnb-teal/30 border-t-rnb-teal rounded-full animate-spin"></div>
        </div>
      </ClientLayout>
    );
  }

  const hoursRemaining = dashboardData?.hours_remaining || 0;
  const monthlyHours = dashboardData?.client?.monthly_hours || 0;
  const hoursUsed = dashboardData?.hours_used || 0;
  const totalAvailable = hoursUsed + hoursRemaining;
  const hoursUsedPercent = totalAvailable > 0 ? (hoursUsed / totalAvailable) * 100 : 0;

  return (
    <ClientLayout activeNav="dashboard">
      <div className="max-w-[1200px] mx-auto">
        {/* Hero Banner */}
        <div className="relative rounded-2xl overflow-hidden mb-6 sm:mb-8 bg-gradient-to-r from-rnb-navy via-[#1e3a5f] to-rnb-blue" data-testid="dashboard-hero">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-72 h-72 bg-rnb-teal rounded-full -translate-y-1/2 translate-x-1/3 blur-3xl" />
            <div className="absolute bottom-0 left-1/3 w-48 h-48 bg-white rounded-full translate-y-1/2 blur-2xl" />
          </div>
          <div className="relative px-5 sm:px-8 py-5 sm:py-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="font-heading text-xl sm:text-2xl font-bold text-white tracking-tight" data-testid="dashboard-greeting">
                Welcome back, {user?.name?.split(" ")[0]}
              </h1>
              <div className="flex items-center gap-2 mt-1.5">
                <Calendar className="w-3.5 h-3.5 text-white/40" />
                <p className="text-xs sm:text-sm text-white/60">Here&apos;s an overview of your account</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <Link to="/portal/my-projects">
                <Button size="sm" className="bg-white/15 hover:bg-white/25 text-white border border-white/20 backdrop-blur-sm rounded-lg gap-1.5 text-xs h-8 sm:h-9" data-testid="hero-projects-btn">
                  <Briefcase className="w-3.5 h-3.5" /> Projects
                </Button>
              </Link>
              <Link to="/portal/invoices">
                <Button size="sm" className="bg-rnb-teal hover:bg-rnb-teal/90 text-white rounded-lg gap-1.5 text-xs h-8 sm:h-9" data-testid="hero-invoices-btn">
                  <FileText className="w-3.5 h-3.5" /> Invoices
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Onboarding Checklist */}
        {showOnboarding && (
          <div className="mb-8">
            <OnboardingChecklist
              dashboardData={dashboardData}
              onComplete={handleDismissOnboarding}
            />
          </div>
        )}

        {/* Become a Partner Banner */}
        <BecomePartnerBanner />

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5 mb-6 sm:mb-8">
          {/* Hours Card */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-card hover:shadow-card-hover transition-shadow p-5" data-testid="hours-card">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-rnb-blue/10 rounded-lg flex items-center justify-center">
                <Clock className="h-5 w-5 text-rnb-blue" />
              </div>
              <Badge variant={hoursRemaining > 5 ? "default" : "destructive"} className="text-xs">
                {hoursRemaining.toFixed(1)}h left
              </Badge>
            </div>
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Monthly Hours</p>
            <p className="font-mono text-2xl font-bold text-rnb-navy">
              {hoursUsed.toFixed(1)} <span className="text-gray-300 text-lg">/</span> <span className="text-lg text-gray-500">{totalAvailable.toFixed(1)}</span>
            </p>
            <Progress value={hoursUsedPercent} className="h-1.5 mt-3" />
          </div>

          {/* Invoices Card */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-card hover:shadow-card-hover transition-shadow p-5" data-testid="invoices-summary-card">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-rnb-teal/10 rounded-lg flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-rnb-teal" />
              </div>
              <Link to="/portal/invoices">
                <Button variant="ghost" size="sm" className="text-xs text-rnb-blue hover:text-rnb-navy">View All</Button>
              </Link>
            </div>
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Pending Invoices</p>
            <p className="font-mono text-2xl font-bold text-rnb-navy">
              {formatCurrency(
                dashboardData?.invoices
                  ?.filter((i) => i.status === "pending")
                  .reduce((sum, i) => sum + i.amount, 0) || 0
              )}
            </p>
          </div>

          {/* Consultant Card */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-card hover:shadow-card-hover transition-shadow p-5" data-testid="consultant-card">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-rnb-navy/10 rounded-lg flex items-center justify-center">
                <User className="h-5 w-5 text-rnb-navy" />
              </div>
              {dashboardData?.consultant && (
                <Link to={`/portal/chat/${dashboardData.consultant.user_id}`}>
                  <Button variant="ghost" size="sm" className="text-xs text-rnb-blue hover:text-rnb-navy">
                    <MessageSquare className="h-3.5 w-3.5 mr-1" /> Chat
                  </Button>
                </Link>
              )}
            </div>
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Your Consultant</p>
            <p className="text-lg font-semibold text-rnb-navy">
              {dashboardData?.consultant?.name || "Not assigned yet"}
            </p>
          </div>
        </div>

        {/* Consultant Profile Card */}
        {dashboardData?.consultant && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-card p-6 mb-8" data-testid="consultant-profile-card">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-4 flex items-center gap-2">
              <User className="h-4 w-4 text-rnb-blue" /> Your Consultant
            </h3>
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
              <Avatar className="h-20 w-20 shrink-0">
                {dashboardData.consultant.profile_photo ? (
                  <AvatarImage src={`data:image/jpeg;base64,${dashboardData.consultant.profile_photo}`} alt={dashboardData.consultant.name} />
                ) : (
                  <AvatarFallback className="bg-gradient-to-br from-rnb-teal to-rnb-blue text-white text-xl font-semibold">
                    {dashboardData.consultant.name?.charAt(0)?.toUpperCase() || "C"}
                  </AvatarFallback>
                )}
              </Avatar>
              <div className="flex-1 text-center sm:text-left">
                <h3 className="text-lg font-semibold text-rnb-navy">{dashboardData.consultant.name}</h3>
                <p className="text-sm text-gray-400 capitalize">{dashboardData.consultant.role?.replace("_", " ") || "Consultant"}</p>
                {dashboardData.consultant.bio && (
                  <p className="text-sm text-rnb-gray mt-2 max-w-xl">{dashboardData.consultant.bio}</p>
                )}
                <div className="flex flex-wrap gap-4 mt-3 justify-center sm:justify-start">
                  {dashboardData.consultant.display_email && (
                    <a href={`mailto:${dashboardData.consultant.display_email}`} className="inline-flex items-center gap-1.5 text-xs text-rnb-blue hover:underline" data-testid="consultant-email-link">
                      <Mail className="h-3.5 w-3.5" /> {dashboardData.consultant.display_email}
                    </a>
                  )}
                  {dashboardData.consultant.phone && (
                    <a href={`tel:${dashboardData.consultant.phone}`} className="inline-flex items-center gap-1.5 text-xs text-rnb-blue hover:underline" data-testid="consultant-phone-link">
                      <Phone className="h-3.5 w-3.5" /> {dashboardData.consultant.phone}
                    </a>
                  )}
                  {dashboardData.consultant.linkedin && (
                    <a href={dashboardData.consultant.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-rnb-blue hover:underline" data-testid="consultant-linkedin-link">
                      <Linkedin className="h-3.5 w-3.5" /> LinkedIn <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mt-3 justify-center sm:justify-start">
                  <Link to={`/portal/chat/${dashboardData.consultant.user_id}`}>
                    <Button size="sm" className="bg-rnb-navy hover:bg-rnb-navy/90 text-white rounded-lg gap-1.5 text-xs h-8" data-testid="message-consultant-btn">
                      <MessageSquare className="h-3.5 w-3.5" /> Send Message
                    </Button>
                  </Link>
                  <Link to="/portal/booking">
                    <Button variant="outline" size="sm" className="rounded-lg gap-1.5 text-xs h-8" data-testid="book-appointment-btn">
                      <Clock className="h-3.5 w-3.5" /> Book Appointment
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
          {/* Project Updates */}
          <div
            className="bg-white rounded-xl border border-gray-100 shadow-card hover:shadow-card-hover transition-shadow cursor-pointer"
            data-testid="project-updates-card"
            onClick={() => navigate("/portal/projects")}
          >
            <div className="px-5 pt-5 pb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-rnb-blue/10 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-4 w-4 text-rnb-blue" />
                </div>
                <span className="text-sm font-semibold text-rnb-navy">Project Updates</span>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-300" />
            </div>
            <div className="px-5 pb-5">
              {(dashboardData?.project_updates?.length > 0 || dashboardData?.project_statuses?.length > 0) ? (
                <div className="space-y-2.5">
                  {dashboardData.project_updates?.slice(0, 3).map((update) => (
                    <div key={update.update_id} className="flex items-start gap-2.5 p-3 bg-[#f8fafc] rounded-lg border border-gray-50">
                      {update.update_type === "milestone" ? (
                        <CheckCircle className="h-3.5 w-3.5 text-rnb-teal mt-0.5 flex-shrink-0" />
                      ) : update.update_type === "blocker" ? (
                        <AlertCircle className="h-3.5 w-3.5 text-yellow-500 mt-0.5 flex-shrink-0" />
                      ) : (
                        <Clock className="h-3.5 w-3.5 text-rnb-blue mt-0.5 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-rnb-navy text-xs truncate">{update.title}</h4>
                        <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-1">{update.content}</p>
                        <p className="text-[11px] text-gray-300 mt-1">{formatDate(update.created_at)}</p>
                      </div>
                    </div>
                  ))}
                  {!dashboardData.project_updates?.length && dashboardData.project_statuses?.slice(0, 3).map((status) => (
                    <div key={status.status_id} className="flex items-start gap-2.5 p-3 bg-[#f8fafc] rounded-lg border border-gray-50">
                      {status.status === "completed" ? (
                        <CheckCircle className="h-3.5 w-3.5 text-rnb-teal mt-0.5 flex-shrink-0" />
                      ) : status.status === "on_hold" ? (
                        <AlertCircle className="h-3.5 w-3.5 text-yellow-500 mt-0.5 flex-shrink-0" />
                      ) : (
                        <Clock className="h-3.5 w-3.5 text-rnb-blue mt-0.5 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-rnb-navy text-xs truncate">{status.title}</h4>
                        <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-1">{status.description}</p>
                        <p className="text-[11px] text-gray-300 mt-1">{formatDate(status.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-8 text-sm">No project updates yet</p>
              )}
            </div>
          </div>

          {/* Recent Invoices */}
          <div
            className="bg-white rounded-xl border border-gray-100 shadow-card hover:shadow-card-hover transition-shadow cursor-pointer"
            data-testid="recent-invoices-card"
            onClick={() => navigate("/portal/invoices")}
          >
            <div className="px-5 pt-5 pb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-rnb-teal/10 rounded-lg flex items-center justify-center">
                  <FileText className="h-4 w-4 text-rnb-teal" />
                </div>
                <span className="text-sm font-semibold text-rnb-navy">Recent Invoices</span>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-300" />
            </div>
            <div className="px-5 pb-5">
              {dashboardData?.invoices?.length > 0 ? (
                <div className="space-y-2.5">
                  {dashboardData.invoices.slice(0, 3).map((invoice) => (
                    <div key={invoice.invoice_id} className="flex items-center justify-between p-3 bg-[#f8fafc] rounded-lg border border-gray-50">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-rnb-navy text-xs truncate">{invoice.description}</p>
                        <p className="text-[11px] text-gray-400">Due {formatDate(invoice.due_date)}</p>
                      </div>
                      <div className="text-right ml-2">
                        <p className="font-mono font-semibold text-rnb-navy text-sm">{formatCurrency(invoice.amount)}</p>
                        <Badge variant={invoice.status === "paid" ? "secondary" : "destructive"} className="text-[10px] mt-0.5">
                          {invoice.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-8 text-sm">No invoices yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </ClientLayout>
  );
};

export default ClientDashboard;
